import type { ChartType, ChartStyle } from "@/lib/chart-settings";
import { registerTradeArrowOverlay } from "./trade-arrow-overlay";
import { registerAdditionalDrawingOverlays } from "./drawings-KLine/overlays";
import { chipFigure } from "./drawings-KLine/overlay-figures";

import { klinecharts } from "./klinecharts-client";

// Type for chart instance (from klinecharts)
export interface ChartInstance {
  createOverlay: (options: any) => void;
  removeOverlay: (id: string) => void;
  overrideOverlay: (options: any) => void;
  createIndicator: (options: any) => string | null;
  getIndicatorByPaneId: (paneId?: string) => any;
  removeIndicator: (paneId: string, indicatorName?: string) => void;
  removePane: (paneId: string) => void;
  setStyles: (styles: any) => void;
  applyNewData: (data: any) => void;
  getDataList?: () => Array<{ timestamp: number; [key: string]: unknown }>;
}

// Transform chart type to KlineCharts candle type
export const getKlineChartType = (chartType: ChartType): string => {
  switch (chartType) {
    case "line":
      return "area";
    case "bars":
      return "ohlc";
    case "candlesticks":
    default:
      return "candle_solid";
  }
};

// Transform data from API format to KlineCharts format
export const transformData = (
  data:
    | Array<{
        time: number;
        open: number;
        high: number;
        low: number;
        close: number;
        volume?: number;
      }>
    | undefined
) => {
  if (!data) return [];
  return data.map((item) => ({
    timestamp: item.time * 1000, // Convert seconds to milliseconds
    open: item.open,
    high: item.high,
    low: item.low,
    close: item.close,
    volume: item.volume || 0,
  }));
};

// Generate chart styles configuration
export const generateChartStyles = (
  chartStyle: ChartStyle | undefined,
  chartType: ChartType,
  pricePrecision?: number,
  showOhlcLegend: boolean = true
) => {
  // If no chartStyle provided, return minimal configuration
  if (!chartStyle) {
    console.warn("No chartStyle provided, using defaults");
  }

  // Determine background color based on scheme ("custom" is the dark default)
  const backgroundColor =
    chartStyle?.scheme === "greenOnBlack" ||
    chartStyle?.scheme === "blackOnWhite" ||
    chartStyle?.scheme === "custom"
      ? "#000000"
      : "#FFFFFF";

  // Base styles configuration
  const styles: any = {
    grid: {
      show: true,
      horizontal: {
        show: true,
        size: 1,
        color: chartStyle?.grid || "#1a1a1a",
        style: "dashed" as const,
        dashValue: [4, 4],
      },
      vertical: {
        show: true,
        size: 1,
        color: chartStyle?.grid || "#1a1a1a",
        style: "dashed" as const,
        dashValue: [4, 4],
      },
    },
    candle: {
      type: getKlineChartType(chartType),
    },
    xAxis: {
      show: true,
      axisLine: {
        show: true,
        color: chartStyle?.grid || "#1a1a1a",
        size: 1,
      },
      tickLine: {
        show: true,
        size: 3,
        length: 3,
        color: chartStyle?.grid || "#1a1a1a",
      },
      tickText: {
        show: true,
        color: chartStyle?.foreground || "#9ca3af",
        size: 12,
        family: "Arial",
        weight: "normal",
        marginStart: 4,
        marginEnd: 4,
      },
    },
    yAxis: {
      show: true,
      type: "normal",
      position: "right",
      precision: pricePrecision || 2, // Add precision here
      axisLine: {
        show: true,
        color: chartStyle?.grid || "#1a1a1a",
        size: 1,
      },
      tickLine: {
        show: true,
        size: 3,
        length: 3,
        color: chartStyle?.grid || "#1a1a1a",
      },
      tickText: {
        show: true,
        color: chartStyle?.foreground || "#9ca3af",
        size: 12,
        family: "Arial",
        weight: "normal",
        marginStart: 4,
        marginEnd: 4,
      },
    },
    separator: {
      size: 1,
      color: chartStyle?.grid || "#1a1a1a",
      fill: true,
      activeBackgroundColor:
        backgroundColor === "#000000"
          ? "rgba(255, 255, 255, 0.15)"
          : "rgba(0, 0, 0, 0.15)",
    },
    crosshair: {
      show: true,
      horizontal: {
        show: true,
        line: {
          show: true,
          style: "dashed" as const,
          dashValue: [4, 2],
          size: 1,
          color: chartStyle?.foreground || "#9ca3af",
        },
        text: {
          show: true,
          color: chartStyle?.foreground || "#FFFFFF",
          size: 12,
          family: "Arial",
          weight: "normal",
          paddingLeft: 2,
          paddingTop: 2,
          paddingRight: 2,
          paddingBottom: 2,
          borderSize: 1,
          borderColor: chartStyle?.grid || "#1a1a1a",
          borderRadius: 2,
          backgroundColor:
            backgroundColor === "#000000" ? "#1a1a1a" : "#666666",
        },
      },
      vertical: {
        show: true,
        line: {
          show: true,
          style: "dashed" as const,
          dashValue: [4, 2],
          size: 1,
          color: chartStyle?.foreground || "#9ca3af",
        },
        text: {
          show: true,
          color: chartStyle?.foreground || "#FFFFFF",
          size: 12,
          family: "Arial",
          weight: "normal",
          paddingLeft: 2,
          paddingTop: 2,
          paddingRight: 2,
          paddingBottom: 2,
          borderSize: 1,
          borderColor: chartStyle?.grid || "#1a1a1a",
          borderRadius: 2,
          backgroundColor:
            backgroundColor === "#000000" ? "#1a1a1a" : "#666666",
        },
      },
    },
    volume: {
      bar: {
        upColor: chartStyle?.volumes || "#26a69a80",
        downColor: chartStyle?.volumes || "#ef535080",
        noChangeColor: chartStyle?.volumes
          ? chartStyle.volumes + "80"
          : "#66666680",
      },
    },
    indicator: {
      VOL: {
        bar: {
          upColor: chartStyle?.volumes || "#26a69a",
          downColor: chartStyle?.volumes || "#ef5350",
          noChangeColor: chartStyle?.volumes
            ? chartStyle.volumes + "80"
            : "#666666",
        },
        tooltip: {
          text: {
            color: chartStyle?.foreground || "#D9D9D9",
          },
        },
      },
      lastValueMark: {
        show: true,
        text: {
          show: true,
          color: chartStyle?.foreground || "#ffffff",
          size: 12,
          family: "Arial",
          weight: "normal",
          paddingLeft: 2,
          paddingTop: 2,
          paddingRight: 2,
          paddingBottom: 2,
          borderRadius: 2,
          backgroundColor: "transparent",
        },
      },
      tooltip: {
        showRule: "always" as any,
        showType: "standard",
        text: {
          size: 12,
          family: "Arial",
          weight: "normal",
          color: chartStyle?.foreground || "#D9D9D9",
          marginLeft: 8,
          marginTop: 6,
          marginRight: 8,
          marginBottom: 0,
        },
      },
    },
    background: {
      color: backgroundColor,
    },
  };

  // Always set area configuration (required by klinecharts)
  styles.candle.area = {
    lineSize: 2,
    lineColor: chartStyle?.lineChart || "#FFFFFF",
    smooth: false,
    value: "close",
    backgroundColor: [
      { offset: 0, color: "rgba(255, 255, 255, 0.1)" },
      { offset: 1, color: "rgba(255, 255, 255, 0.01)" },
    ],
  };

  // Configure candle styles based on chart type
  if (chartType === "candlesticks") {
    // For candlesticks: barUp/barDown for borders and wicks, bullCandle/bearCandle for fill
    const upFillColor = chartStyle?.bullCandle || "#FFFFFF";
    const downFillColor = chartStyle?.bearCandle || "#000000";
    const upBorderColor = chartStyle?.barUp || "#000000";
    const downBorderColor = chartStyle?.barDown || "#808080";

    styles.candle.bar = {
      upColor:
        upFillColor === "#FFFFFF" && backgroundColor === "#FFFFFF"
          ? "transparent"
          : upFillColor,
      downColor: downFillColor,
      noChangeColor: "#666666",
      upBorderColor: upBorderColor,
      downBorderColor: downBorderColor,
      noChangeBorderColor: "#666666",
      upWickColor: upBorderColor,
      downWickColor: downBorderColor,
      noChangeWickColor: "#666666",
    };
  } else if (chartType === "bars") {
    // For OHLC bars: use barUp/barDown colors with fallback logic
    let upColor = chartStyle?.barUp || "#000000";
    let downColor = chartStyle?.barDown || "#808080";

    // If barUp and barDown are the same, try to use different colors
    if (upColor === downColor && chartStyle) {
      // Try using bullCandle/bearCandle colors as fallback
      if (chartStyle.bullCandle !== chartStyle.bearCandle) {
        upColor = chartStyle.barUp;
        downColor = chartStyle.barDown;
      }
    }

    // For OHLC bars, we need to set bar colors
    styles.candle.bar = {
      upColor: upColor,
      downColor: downColor,
      noChangeColor: "#666666",
      upBorderColor: upColor,
      downBorderColor: downColor,
      noChangeBorderColor: "#666666",
      upWickColor: upColor,
      downWickColor: downColor,
      noChangeWickColor: "#666666",
    };
  } else if (chartType === "line") {
    // For line chart: override area configuration with specific line chart settings
    const lineColor = chartStyle?.lineChart || "#FFFFFF";

    styles.candle.area = {
      lineSize: 2,
      lineColor: lineColor,
      smooth: false,
      value: "close",
      backgroundColor: [
        {
          offset: 0,
          color:
            lineColor === "#FFFFFF" && backgroundColor === "#FFFFFF"
              ? "rgba(0, 0, 0, 0.1)"
              : lineColor + "1A",
        },
        {
          offset: 1,
          color:
            lineColor === "#FFFFFF" && backgroundColor === "#FFFFFF"
              ? "rgba(0, 0, 0, 0.01)"
              : lineColor + "03",
        },
      ],
    };
  }

  // Common price mark configuration
  styles.candle.priceMark = {
    show: true,
    high: {
      show: true,
      color: chartStyle?.foreground || "#D9D9D9",
      textSize: 12,
      textFamily: "Arial",
      textWeight: "normal",
    },
    low: {
      show: true,
      color: chartStyle?.foreground || "#D9D9D9",
      textSize: 12,
      textFamily: "Arial",
      textWeight: "normal",
    },
    last: {
      show: true,
      upColor:
        chartType === "line"
          ? chartStyle?.lineChart || "#2196F3"
          : chartStyle?.barUp || "#26a69a",
      downColor:
        chartType === "line"
          ? chartStyle?.lineChart || "#2196F3"
          : chartStyle?.barDown || "#ef5350",
      noChangeColor: "#666666",
      line: {
        show: true,
        style: "dashed" as const,
        size: 1,
        dashValue: [4, 4],
      },
      text: {
        show: true,
        size: 12,
        paddingLeft: 2,
        paddingTop: 2,
        paddingRight: 2,
        paddingBottom: 2,
        borderRadius: 2,
        borderSize: 1,
        borderColor: "transparent",
        color: "#FFFFFF",
        backgroundColor: "transparent",
      },
    },
  };

  // Tooltip configuration based on chart type.
  // The "OHLC Data" chart setting controls whether the candle legend
  // ("Time / Open / High / Low / Close / Volume") is rendered on the chart.
  // The tooltip itself always renders so the crosshair's Time row stays
  // visible; with the OHLC legend turned off we trim the labels down to just
  // "Time" instead of hiding the whole tooltip.
  styles.candle.tooltip = {
    showRule: "always" as any,
    showType: "standard",
    labels: !showOhlcLegend
      ? ["Time"]
      : chartType === "line"
        ? ["Time", "Close"]
        : ["Time", "Open", "High", "Low", "Close", "Volume"],
    text: {
      size: 12,
      family: "Arial",
      weight: "normal",
      color: chartStyle?.foreground || "#D9D9D9",
      marginLeft: 8,
      marginTop: 6,
      marginRight: 8,
      marginBottom: 0,
    },
  };

  return styles;
};


// Register custom rectangle overlay (also used for ellipse)
export const registerCustomRectOverlay = () => {
  if (typeof window === "undefined" || !klinecharts) return;

  try {
    klinecharts.registerOverlay({
      name: "customRect",
      totalStep: 3,
      needDefaultPointFigure: true,
      needDefaultXAxisFigure: false,
      needDefaultYAxisFigure: false,
      createPointFigures: ({
        coordinates,
        overlay,
      }: {
        coordinates: Array<{ x: number; y: number }>;
        overlay?: any;
      }) => {
        if (coordinates.length >= 2) {
          const x = Math.min(coordinates[0].x, coordinates[1].x);
          const y = Math.min(coordinates[0].y, coordinates[1].y);
          const width = Math.abs(coordinates[1].x - coordinates[0].x);
          const height = Math.abs(coordinates[1].y - coordinates[0].y);

          // Get style from overlay extendData
          const settings = overlay?.extendData?.settings || {};
          const style = settings.style || {};
          const fill = settings.fill !== false;
          const lineStyle = style.lineStyle || "solid";

          return [
            {
              type: "rect",
              attrs: { x, y, width, height },
              styles: {
                style: fill ? "stroke_fill" : "stroke",
                color: style.fillColor || "rgba(255, 0, 0, 0.25)",
                borderColor: style.borderColor || "#FF0000",
                borderSize: style.lineWidth || 1,
                borderStyle: lineStyle === "dotted" ? "dashed" : lineStyle,
                borderDashedValue:
                  lineStyle === "dotted"
                    ? [2, 2]
                    : lineStyle === "dashed"
                    ? [5, 5]
                    : undefined,
              },
            },
          ];
        }
        return [];
      },
    });
  } catch (error) {}
};

// Register custom ellipse overlay using direct canvas drawing
export const registerCustomEllipseOverlay = () => {
  if (typeof window === "undefined" || !klinecharts) return;

  try {
    klinecharts.registerOverlay({
      name: "customEllipse",
      totalStep: 3,
      needDefaultPointFigure: true,
      needDefaultXAxisFigure: false,
      needDefaultYAxisFigure: false,
      createPointFigures: ({
        coordinates,
        overlay,
      }: {
        coordinates: Array<{ x: number; y: number }>;
        overlay?: any;
      }) => {
        if (coordinates.length >= 2) {
          const x1 = Math.min(coordinates[0].x, coordinates[1].x);
          const y1 = Math.min(coordinates[0].y, coordinates[1].y);
          const x2 = Math.max(coordinates[0].x, coordinates[1].x);
          const y2 = Math.max(coordinates[0].y, coordinates[1].y);

          // Get style from overlay extendData
          const settings = overlay?.extendData?.settings || {};
          const style = settings.style || {};
          const fill = settings.fill !== false;
          const lineStyle = style.lineStyle || "solid";

          // Return a custom draw function
          return [
            {
              type: "rect",
              attrs: { x: x1, y: y1, width: x2 - x1, height: y2 - y1 },
              styles: {
                style: fill ? "stroke_fill" : "stroke",
                color: style.fillColor || "rgba(255, 0, 0, 0.25)",
                borderColor: style.borderColor || "#FF0000",
                borderSize: style.lineWidth || 1,
                borderStyle: lineStyle === "dotted" ? "dashed" : lineStyle,
                borderDashedValue:
                  lineStyle === "dotted"
                    ? [2, 2]
                    : lineStyle === "dashed"
                    ? [5, 5]
                    : undefined,
                borderRadius: 1000, // This creates rounded corners for ellipse effect
              },
            },
          ];
        }
        return [];
      },
      drawExtend: ({ ctx, coordinates, styles }: any) => {
        // Custom drawing for ellipse
        if (coordinates && coordinates.length >= 2) {
          const x1 = Math.min(coordinates[0].x, coordinates[1].x);
          const y1 = Math.min(coordinates[0].y, coordinates[1].y);
          const x2 = Math.max(coordinates[0].x, coordinates[1].x);
          const y2 = Math.max(coordinates[0].y, coordinates[1].y);
          const centerX = (x1 + x2) / 2;
          const centerY = (y1 + y2) / 2;
          const radiusX = (x2 - x1) / 2;
          const radiusY = (y2 - y1) / 2;

          ctx.save();
          ctx.beginPath();
          ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);

          // Apply styles
          const overlayStyles = styles || {};
          if (
            overlayStyles.style === "stroke_fill" ||
            overlayStyles.style === "fill"
          ) {
            ctx.fillStyle = overlayStyles.color || "rgba(255, 0, 0, 0.25)";
            ctx.fill();
          }

          ctx.strokeStyle = overlayStyles.borderColor || "#FF0000";
          ctx.lineWidth = overlayStyles.borderSize || 1;
          ctx.stroke();
          ctx.restore();
        }
      },
    });
  } catch (error) {}
};

// Register custom triangle overlay
export const registerCustomTriangleOverlay = () => {
  if (typeof window === "undefined" || !klinecharts) return;

  try {
    klinecharts.registerOverlay({
      name: "customTriangle",
      totalStep: 4, // 3 points to create a triangle
      needDefaultPointFigure: true,
      needDefaultXAxisFigure: false,
      needDefaultYAxisFigure: false,
      createPointFigures: ({
        coordinates,
        overlay,
      }: {
        coordinates: Array<{ x: number; y: number }>;
        overlay?: any;
      }) => {
        if (coordinates.length >= 3) {
          // Get settings from overlay extendData
          const settings = overlay?.extendData?.settings || {};
          const style = settings.style || {};
          const fill = settings.fill !== false;
          const lineStyle = style.lineStyle || "solid";

          // If fill is enabled, use polygon type instead of lines
          if (fill) {
            return [
              {
                type: "polygon",
                attrs: {
                  coordinates: [
                    { x: coordinates[0].x, y: coordinates[0].y },
                    { x: coordinates[1].x, y: coordinates[1].y },
                    { x: coordinates[2].x, y: coordinates[2].y },
                  ],
                },
                styles: {
                  style: "stroke_fill",
                  color: style.fillColor || "rgba(255, 0, 0, 0.25)",
                  borderColor: style.borderColor || "#FF0000",
                  borderSize: style.lineWidth || 1,
                  borderStyle: lineStyle === "dotted" ? "dashed" : lineStyle,
                  borderDashedValue:
                    lineStyle === "dotted"
                      ? [2, 2]
                      : lineStyle === "dashed"
                      ? [5, 5]
                      : undefined,
                },
              },
            ];
          }

          // If no fill, create three lines to form a triangle
          const figures = [];

          // Line from point 1 to point 2
          figures.push({
            type: "line",
            attrs: {
              coordinates: [
                { x: coordinates[0].x, y: coordinates[0].y },
                { x: coordinates[1].x, y: coordinates[1].y },
              ],
            },
            styles: {
              style: style.lineStyle || "solid",
              color: style.borderColor || "#FF0000",
              size: style.lineWidth || 1,
              dashedValue:
                style.lineStyle === "dashed"
                  ? [5, 5]
                  : style.lineStyle === "dotted"
                  ? [2, 2]
                  : [0, 0],
            },
          });

          // Line from point 2 to point 3
          figures.push({
            type: "line",
            attrs: {
              coordinates: [
                { x: coordinates[1].x, y: coordinates[1].y },
                { x: coordinates[2].x, y: coordinates[2].y },
              ],
            },
            styles: {
              style: style.lineStyle || "solid",
              color: style.borderColor || "#FF0000",
              size: style.lineWidth || 1,
              dashedValue:
                style.lineStyle === "dashed"
                  ? [5, 5]
                  : style.lineStyle === "dotted"
                  ? [2, 2]
                  : [0, 0],
            },
          });

          // Line from point 3 to point 1 (closing the triangle)
          figures.push({
            type: "line",
            attrs: {
              coordinates: [
                { x: coordinates[2].x, y: coordinates[2].y },
                { x: coordinates[0].x, y: coordinates[0].y },
              ],
            },
            styles: {
              style: style.lineStyle || "solid",
              color: style.borderColor || "#FF0000",
              size: style.lineWidth || 1,
              dashedValue:
                style.lineStyle === "dashed"
                  ? [5, 5]
                  : style.lineStyle === "dotted"
                  ? [2, 2]
                  : [0, 0],
            },
          });

          return figures;
        } else if (coordinates.length === 2) {
          // Draw a line while placing the third point
          return [
            {
              type: "line",
              attrs: {
                coordinates: [
                  { x: coordinates[0].x, y: coordinates[0].y },
                  { x: coordinates[1].x, y: coordinates[1].y },
                ],
              },
              styles: {
                style: "dashed",
                color: "#FF0000",
                size: 1,
                dashedValue: [5, 5],
              },
            },
          ];
        }
        return [];
      },
      // Add drawExtend for fill functionality
      drawExtend: ({ ctx, coordinates, overlay }: any) => {
        if (coordinates && coordinates.length >= 3) {
          const settings = overlay?.extendData?.settings || {};
          const style = settings.style || {};

          // Only draw fill if enabled
          if (settings.fill !== false) {
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(coordinates[0].x, coordinates[0].y);
            ctx.lineTo(coordinates[1].x, coordinates[1].y);
            ctx.lineTo(coordinates[2].x, coordinates[2].y);
            ctx.closePath();

            ctx.fillStyle = style.fillColor || "rgba(255, 0, 0, 0.25)";
            ctx.fill();
            ctx.restore();
          }
        }
      },
    });
  } catch (error) {
  }
};

// Register custom arrowed line overlay
export const registerCustomArrowedLineOverlay = () => {
  if (typeof window === "undefined" || !klinecharts) return;

  try {
    klinecharts.registerOverlay({
      name: "customArrowedLine",
      totalStep: 3, // 2 points to create a line with arrow
      needDefaultPointFigure: true,
      needDefaultXAxisFigure: false,
      needDefaultYAxisFigure: false,
      createPointFigures: ({
        coordinates,
        overlay,
      }: {
        coordinates: Array<{ x: number; y: number }>;
        overlay?: any;
      }) => {
        if (coordinates.length >= 2) {
          // Get settings from overlay extendData
          const settings = overlay?.extendData?.settings || {};
          const style = settings.style || {};

          const figures = [];

          // Main line
          figures.push({
            type: "line",
            attrs: {
              coordinates: [
                { x: coordinates[0].x, y: coordinates[0].y },
                { x: coordinates[1].x, y: coordinates[1].y },
              ],
            },
            styles: {
              style: style.lineStyle || "solid",
              color: style.color || "#FF0000",
              size: style.lineWidth || 2,
              dashedValue:
                style.lineStyle === "dashed"
                  ? [5, 5]
                  : style.lineStyle === "dotted"
                  ? [2, 2]
                  : [0, 0],
            },
          });

          // Calculate arrow head
          const x1 = coordinates[0].x;
          const y1 = coordinates[0].y;
          const x2 = coordinates[1].x;
          const y2 = coordinates[1].y;

          const angle = Math.atan2(y2 - y1, x2 - x1);
          const arrowLength = style.arrowSize || 15;
          const arrowAngle = style.arrowAngle || Math.PI / 6;

          // Calculate arrow head points
          const arrowX1 = x2 - arrowLength * Math.cos(angle - arrowAngle);
          const arrowY1 = y2 - arrowLength * Math.sin(angle - arrowAngle);
          const arrowX2 = x2 - arrowLength * Math.cos(angle + arrowAngle);
          const arrowY2 = y2 - arrowLength * Math.sin(angle + arrowAngle);

          // Add arrow head as polygon
          figures.push({
            type: "polygon",
            attrs: {
              coordinates: [
                { x: x2, y: y2 },
                { x: arrowX1, y: arrowY1 },
                { x: arrowX2, y: arrowY2 },
              ],
            },
            styles: {
              style: "fill",
              color: style.color || "#FF0000",
            },
          });

          return figures;
        }
        return [];
      },
    });
  } catch (error) {}
};

// Register custom equidistant channel overlay
export const registerCustomEquidistantChannelOverlay = () => {
  if (typeof window === "undefined" || !klinecharts) return;

  try {
    klinecharts.registerOverlay({
      name: "customEquidistantChannel",
      totalStep: 4, // 3 points: 2 for main line, 1 for parallel distance
      needDefaultPointFigure: true,
      needDefaultXAxisFigure: false,
      needDefaultYAxisFigure: false,
      createPointFigures: ({
        coordinates,
        overlay,
        bounding,
      }: {
        coordinates: Array<{ x: number; y: number }>;
        overlay?: any;
        bounding?: any;
      }) => {
        if (coordinates.length >= 3) {
          const settings = overlay?.extendData?.settings || {};
          const style = settings.style || {};

          const figures = [];

          // Calculate the parallel line
          // Points: A (coordinates[0]), B (coordinates[1]), C (coordinates[2])
          const ax = coordinates[0].x;
          const ay = coordinates[0].y;
          const bx = coordinates[1].x;
          const by = coordinates[1].y;
          const cx = coordinates[2].x;
          const cy = coordinates[2].y;

          // Vector from A to B
          const vx = bx - ax;
          const vy = by - ay;

          // Distance from line AB to point C
          const distance =
            ((cx - ax) * -vy + (cy - ay) * vx) / Math.sqrt(vx * vx + vy * vy);

          // Calculate parallel line points - use + to put line on same side as point C
          const dx = ax + (distance * -vy) / Math.sqrt(vx * vx + vy * vy);
          const dy = ay + (distance * vx) / Math.sqrt(vx * vx + vy * vy);
          const ex = bx + (distance * -vy) / Math.sqrt(vx * vx + vy * vy);
          const ey = by + (distance * vx) / Math.sqrt(vx * vx + vy * vy);

          // Function to extend line far beyond visible area
          const extendLine = (
            x1: number,
            y1: number,
            x2: number,
            y2: number
          ) => {
            const dx = x2 - x1;
            const dy = y2 - y1;

            // Normalize direction vector
            const length = Math.sqrt(dx * dx + dy * dy);
            if (length === 0)
              return [
                { x: x1, y: y1 },
                { x: x2, y: y2 },
              ];

            const dirX = dx / length;
            const dirY = dy / length;

            // Extend the line by a large factor in both directions
            const extensionFactor = 10000; // Very large to ensure it covers the whole chart

            return [
              {
                x: x1 - dirX * extensionFactor,
                y: y1 - dirY * extensionFactor,
              },
              {
                x: x2 + dirX * extensionFactor,
                y: y2 + dirY * extensionFactor,
              },
            ];
          };

          // Extend main line (A to B) far beyond visible area
          const mainLineExtended = extendLine(ax, ay, bx, by);
          figures.push({
            type: "line",
            attrs: {
              coordinates: mainLineExtended,
            },
            styles: {
              style: style.lineStyle || "solid",
              color: style.color || "#2196F3",
              size: style.lineWidth || 1,
              dashedValue:
                style.lineStyle === "dashed"
                  ? [5, 5]
                  : style.lineStyle === "dotted"
                  ? [2, 2]
                  : [0, 0],
            },
          });

          // Extend parallel line (D to E) far beyond visible area
          const parallelLineExtended = extendLine(dx, dy, ex, ey);
          figures.push({
            type: "line",
            attrs: {
              coordinates: parallelLineExtended,
            },
            styles: {
              style: style.lineStyle || "solid",
              color: style.color || "#2196F3",
              size: style.lineWidth || 1,
              dashedValue:
                style.lineStyle === "dashed"
                  ? [5, 5]
                  : style.lineStyle === "dotted"
                  ? [2, 2]
                  : [0, 0],
            },
          });

          // No connecting dashed lines - removed as requested

          return figures;
        } else if (coordinates.length === 2) {
          // Just show the main line while drawing
          const settings = overlay?.extendData?.settings || {};
          const style = settings.style || {};

          return [
            {
              type: "line",
              attrs: {
                coordinates: [
                  { x: coordinates[0].x, y: coordinates[0].y },
                  { x: coordinates[1].x, y: coordinates[1].y },
                ],
              },
              styles: {
                style: "dashed",
                color: style.color || "#2196F3",
                size: 1,
                dashedValue: [5, 5],
              },
            },
          ];
        }
        return [];
      },
      drawExtend: ({ ctx, coordinates, overlay }: any) => {
        // Equidistant channel doesn't support fill - removed as requested
        return;
      },
    });
  } catch (error) {}
};

// Register custom arrowed line overlay
// Register custom Elliott Motive Wave overlay
export const registerCustomElliottMotiveWaveOverlay = () => {
  if (typeof window === "undefined" || !klinecharts) return;

  try {
    klinecharts.registerOverlay({
      name: "customElliottMotiveWave",
      totalStep: 6, // 5 points for waves 1-5
      needDefaultPointFigure: true,
      needDefaultXAxisFigure: false,
      needDefaultYAxisFigure: false,
      createPointFigures: ({
        coordinates,
        overlay,
      }: {
        coordinates: Array<{ x: number; y: number }>;
        overlay?: any;
      }) => {
        const settings = overlay?.extendData?.settings || {};
        const style = settings.style || {};
        const figures = [];

        // Draw lines connecting consecutive points
        if (coordinates.length >= 2) {
          for (let i = 0; i < coordinates.length - 1; i++) {
            figures.push({
              type: "line",
              attrs: {
                coordinates: [
                  { x: coordinates[i].x, y: coordinates[i].y },
                  { x: coordinates[i + 1].x, y: coordinates[i + 1].y },
                ],
              },
              styles: {
                style: style.lineStyle || "solid",
                color: style.color || "#FF0000",
                size: style.lineWidth || 2,
                dashedValue:
                  style.lineStyle === "dashed"
                    ? [5, 5]
                    : style.lineStyle === "dotted"
                    ? [2, 2]
                    : [0, 0],
              },
            });
          }
        }

        // Add wave labels if enabled
        if (style.showLabels !== false && coordinates.length > 0) {
          const waveLabels = ["1", "2", "3", "4", "5"];

          for (let i = 0; i < coordinates.length && i < 5; i++) {
            const labelOffset = style.labelOffset || 15;
            // Position labels above for odd waves (1, 3, 5) and below for even waves (2, 4)
            const yOffset = i % 2 === 0 ? -labelOffset : labelOffset;

            figures.push({
              type: "text",
              attrs: {
                x: coordinates[i].x,
                y: coordinates[i].y + yOffset,
                text: waveLabels[i],
                align: "center",
                baseline: "middle",
              },
              styles: {
                style: "fill",
                color: "#FFFFFF", // White text on colored background
                size: style.labelSize || 12,
                family: style.labelFont || "Arial",
                weight: style.labelWeight || "bold",
                backgroundColor: style.color || "#FF0000", // Same color as lines
                paddingLeft: 4,
                paddingRight: 4,
                paddingTop: 2,
                paddingBottom: 2,
                borderRadius: 2,
              },
            });
          }
        }

        return figures;
      },
      drawExtend: ({ ctx, coordinates, overlay }: any) => {
        // Optional: Draw additional visual elements like trend channel
        if (coordinates && coordinates.length >= 5) {
          const settings = overlay?.extendData?.settings || {};
          const style = settings.style || {};

          // Draw trend channel if enabled
          if (style.showTrendChannel === true) {
            ctx.save();

            // Find the trend line from wave 1 to wave 3
            const wave1 = coordinates[0];
            const wave3 = coordinates[2];
            const wave5 = coordinates[4];

            // Draw upper trend line through waves 1, 3, and potentially 5
            ctx.beginPath();
            ctx.moveTo(wave1.x, wave1.y);
            ctx.lineTo(wave3.x, wave3.y);

            // Extend the line to wave 5
            const slope = (wave3.y - wave1.y) / (wave3.x - wave1.x);
            const projectedY = wave3.y + slope * (wave5.x - wave3.x);
            ctx.lineTo(wave5.x, projectedY);

            // Apply semi-transparent version of main color
            const mainColor = style.color || "#FF0000";
            const r = parseInt(mainColor.slice(1, 3), 16);
            const g = parseInt(mainColor.slice(3, 5), 16);
            const b = parseInt(mainColor.slice(5, 7), 16);
            ctx.strokeStyle =
              style.trendChannelColor || `rgba(${r}, ${g}, ${b}, 0.5)`;
            ctx.lineWidth = 1;
            ctx.setLineDash([5, 5]);
            ctx.stroke();

            // Draw lower trend line through wave 2 and 4
            if (coordinates.length >= 4) {
              const wave2 = coordinates[1];
              const wave4 = coordinates[3];

              ctx.beginPath();
              ctx.moveTo(wave2.x, wave2.y);
              ctx.lineTo(wave4.x, wave4.y);

              ctx.stroke();
            }

            ctx.restore();
          }
        }
      },
    });
  } catch (error) {}
};

// Register custom Gann Grid overlay
export const registerCustomGannGridOverlay = () => {
  if (typeof window === "undefined" || !klinecharts) return;

  try {
    klinecharts.registerOverlay({
      name: "customGannGrid",
      totalStep: 3, // 2 points to create rectangle
      needDefaultPointFigure: true,
      needDefaultXAxisFigure: false,
      needDefaultYAxisFigure: false,
      createPointFigures: ({
        coordinates,
        overlay,
      }: {
        coordinates: Array<{ x: number; y: number }>;
        overlay?: any;
      }) => {
        const settings = overlay?.extendData?.settings || {};
        const style = settings.style || {};
        const figures = [];

        if (coordinates.length === 2) {
          // Calculate rectangle bounds from diagonal points
          const x1 = Math.min(coordinates[0].x, coordinates[1].x);
          const y1 = Math.min(coordinates[0].y, coordinates[1].y);
          const x2 = Math.max(coordinates[0].x, coordinates[1].x);
          const y2 = Math.max(coordinates[0].y, coordinates[1].y);

          // Draw main rectangle
          figures.push({
            type: "rect",
            attrs: {
              x: x1,
              y: y1,
              width: x2 - x1,
              height: y2 - y1,
            },
            styles: {
              style: "stroke",
              borderColor: style.color || "#FF0000",
              borderSize: style.lineWidth || 1,
              borderStyle:
                style.lineStyle === "dotted"
                  ? "dashed"
                  : style.lineStyle || "solid",
              borderDashedValue:
                style.lineStyle === "dotted"
                  ? [2, 2]
                  : style.lineStyle === "dashed"
                  ? [5, 5]
                  : undefined,
            },
          });

          // Draw both diagonals
          figures.push({
            type: "line",
            attrs: {
              coordinates: [
                { x: x1, y: y1 },
                { x: x2, y: y2 },
              ],
            },
            styles: {
              style: style.lineStyle || "solid",
              color: style.color || "#FF0000",
              size: style.lineWidth || 1,
              dashedValue:
                style.lineStyle === "dashed"
                  ? [5, 5]
                  : style.lineStyle === "dotted"
                  ? [2, 2]
                  : [0, 0],
            },
          });

          figures.push({
            type: "line",
            attrs: {
              coordinates: [
                { x: x1, y: y2 },
                { x: x2, y: y1 },
              ],
            },
            styles: {
              style: style.lineStyle || "solid",
              color: style.color || "#FF0000",
              size: style.lineWidth || 1,
              dashedValue:
                style.lineStyle === "dashed"
                  ? [5, 5]
                  : style.lineStyle === "dotted"
                  ? [2, 2]
                  : [0, 0],
            },
          });

          // Draw dashed lines from top-left corner (x1, y1)
          // To 1/4 of bottom line
          figures.push({
            type: "line",
            attrs: {
              coordinates: [
                { x: x1, y: y1 },
                { x: x1 + (x2 - x1) * 0.25, y: y2 },
              ],
            },
            styles: {
              style: "dashed",
              color: style.color || "#FF0000",
              size: 1,
              dashedValue: [5, 5],
            },
          });

          // To 1/2 of bottom line
          figures.push({
            type: "line",
            attrs: {
              coordinates: [
                { x: x1, y: y1 },
                { x: x1 + (x2 - x1) * 0.5, y: y2 },
              ],
            },
            styles: {
              style: "dashed",
              color: style.color || "#FF0000",
              size: 1,
              dashedValue: [5, 5],
            },
          });

          // To 1/2 of right line
          figures.push({
            type: "line",
            attrs: {
              coordinates: [
                { x: x1, y: y1 },
                { x: x2, y: y1 + (y2 - y1) * 0.5 },
              ],
            },
            styles: {
              style: "dashed",
              color: style.color || "#FF0000",
              size: 1,
              dashedValue: [5, 5],
            },
          });

          // To 3/4 of right line
          figures.push({
            type: "line",
            attrs: {
              coordinates: [
                { x: x1, y: y1 },
                { x: x2, y: y1 + (y2 - y1) * 0.75 },
              ],
            },
            styles: {
              style: "dashed",
              color: style.color || "#FF0000",
              size: 1,
              dashedValue: [5, 5],
            },
          });

          // Draw dashed lines from bottom-left corner (x1, y2)
          // To 1/4 of top line
          figures.push({
            type: "line",
            attrs: {
              coordinates: [
                { x: x1, y: y2 },
                { x: x1 + (x2 - x1) * 0.25, y: y1 },
              ],
            },
            styles: {
              style: "dashed",
              color: style.color || "#FF0000",
              size: 1,
              dashedValue: [5, 5],
            },
          });

          // To 1/2 of top line
          figures.push({
            type: "line",
            attrs: {
              coordinates: [
                { x: x1, y: y2 },
                { x: x1 + (x2 - x1) * 0.5, y: y1 },
              ],
            },
            styles: {
              style: "dashed",
              color: style.color || "#FF0000",
              size: 1,
              dashedValue: [5, 5],
            },
          });

          // To 1/4 of right line
          figures.push({
            type: "line",
            attrs: {
              coordinates: [
                { x: x1, y: y2 },
                { x: x2, y: y1 + (y2 - y1) * 0.25 },
              ],
            },
            styles: {
              style: "dashed",
              color: style.color || "#FF0000",
              size: 1,
              dashedValue: [5, 5],
            },
          });

          // To 1/2 of right line
          figures.push({
            type: "line",
            attrs: {
              coordinates: [
                { x: x1, y: y2 },
                { x: x2, y: y1 + (y2 - y1) * 0.5 },
              ],
            },
            styles: {
              style: "dashed",
              color: style.color || "#FF0000",
              size: 1,
              dashedValue: [5, 5],
            },
          });
        }

        return figures;
      },
      drawExtend: ({ ctx, coordinates, overlay }: any) => {
        // All drawing is done in createPointFigures
        // This function is kept empty to maintain overlay structure
      },
    });
  } catch (error) {}
};

// Register custom Text overlay
export const registerCustomTextOverlay = () => {
  if (typeof window === "undefined" || !klinecharts) return;

  try {
    klinecharts.registerOverlay({
      name: "customText",
      totalStep: 2, // 1 point to place text
      needDefaultPointFigure: true,
      needDefaultXAxisFigure: false,
      needDefaultYAxisFigure: false,
      createPointFigures: ({
        coordinates,
        overlay,
      }: {
        coordinates: Array<{ x: number; y: number }>;
        overlay?: any;
      }) => {
        const settings = overlay?.extendData?.settings || {};
        const style = settings.style || {};
        const figures = [];

        if (coordinates.length === 1 && settings.text) {
          // Draw text at the clicked position
          figures.push({
            type: "text",
            attrs: {
              x: coordinates[0].x,
              y: coordinates[0].y,
              text: settings.text || "Text",
              align: style.align || "center",
              baseline: style.baseline || "middle",
            },
            styles: {
              style: "fill",
              color: style.color || "#FF0000",
              size: style.fontSize || 14,
              family: style.fontFamily || "Arial",
              weight: style.fontWeight || "normal",
              backgroundColor: style.backgroundColor || "transparent",
              paddingLeft: style.paddingLeft || 4,
              paddingRight: style.paddingRight || 4,
              paddingTop: style.paddingTop || 2,
              paddingBottom: style.paddingBottom || 2,
              borderRadius: style.borderRadius || 0,
              borderStyle: style.borderStyle || "solid",
              borderColor: style.borderColor || "transparent",
              borderSize: style.borderSize || 0,
            },
          });
        }

        return figures;
      },
      drawExtend: ({ ctx, coordinates, overlay }: any) => {
        // All drawing is done in createPointFigures
      },
    });
  } catch (error) {}
};

// Register custom price-label overlay (rounded tag with text at a (timestamp, price) point)
export const registerCustomPriceLabelOverlay = () => {
  if (typeof window === "undefined" || !klinecharts) return;

  try {
    klinecharts.registerOverlay({
      name: "customPriceLabel",
      totalStep: 2,
      needDefaultPointFigure: true,
      needDefaultXAxisFigure: false,
      needDefaultYAxisFigure: false,
      createPointFigures: ({
        coordinates,
        overlay,
      }: {
        coordinates: Array<{ x: number; y: number }>;
        overlay?: any;
      }) => {
        if (coordinates.length < 1) return [];
        const settings = overlay?.extendData?.settings || {};
        const style = settings.style || {};
        const text =
          typeof settings.text === "string" && settings.text.length > 0
            ? settings.text
            : typeof overlay?.points?.[0]?.value === "number"
              ? overlay.points[0].value.toFixed(5)
              : "";

        // One colour drives the whole chip, with an auto-inked caption.
        return chipFigure(coordinates[0].x, coordinates[0].y, text, style, {
          align: "center",
          fontSize: style.fontSize || 11,
          paddingX: 6,
          paddingY: 3,
        });
      },
    });
  } catch (error) {
    void error;
  }
};

// Register all custom overlays at once
export const registerAllCustomOverlays = () => {
  registerCustomRectOverlay();
  registerCustomEllipseOverlay();
  registerCustomTriangleOverlay();
  registerCustomArrowedLineOverlay();
  registerCustomEquidistantChannelOverlay();
  registerCustomElliottMotiveWaveOverlay();
  registerCustomGannGridOverlay();
  registerCustomTextOverlay();
  registerCustomPriceLabelOverlay();
  registerTradeArrowOverlay();
  registerAdditionalDrawingOverlays();
};
